import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, Alert } from '@mui/material';
import { Typography, Card, Button } from '../design-system';
import { DESIGN_TOKENS } from '../design-system/tokens/colors';
import { useAuth } from '../hooks/useAuth';
import { asaasService } from '../services/asaasService';

const SubscriptionDetailsPage: React.FC = () => {
  const { authState } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [subscription, setSubscription] = useState<any | null>(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try {
        setLoading(true);
        setError(null);
        const email = authState.user?.email || '';
        if (!email) throw new Error('Usuário sem email');
        const access = await asaasService.checkAccessByEmail(email);
        const sub = (access as any)?.subscription;
        if (sub?.id) {
          const detail = await asaasService.getSubscription(sub.id);
          if (!mounted) return;
          setSubscription(detail);
        } else {
          if (!mounted) return;
          setSubscription(null);
        }
      } catch (e: any) {
        if (!mounted) return;
        setError(e?.message || 'Falha ao carregar assinatura');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [authState.user?.email]);

  const s = subscription || {};

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: DESIGN_TOKENS.colors.background.default, p: 4 }}>
      <Card variant="outlined" padding="large">
        <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>Detalhes da Assinatura</Typography>
        {loading && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <CircularProgress sx={{ color: DESIGN_TOKENS.colors.primary[500] }} />
            <Typography variant="body1">Carregando...</Typography>
          </Box>
        )}
        {!loading && error && (
          <Alert severity="error">{error}</Alert>
        )}
        {!loading && !error && !subscription && (
          <Alert severity="info">Você não possui uma assinatura ativa no momento.</Alert>
        )}
        {!loading && !error && subscription && (
          <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }, gap: 3 }}>
            <Box>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 700 }}>ID da Assinatura</Typography>
              <Typography variant="body1">{s?.id || '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 700 }}>Plano</Typography>
              <Typography variant="body1">{s?.description || 'Plano MUU'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 700 }}>Status</Typography>
              <Typography variant="body1">{(s?.status || 'ACTIVE').toString()}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 700 }}>Valor</Typography>
              <Typography variant="body1">R$ {Number(s?.value || 30).toFixed(2)}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 700 }}>Data da Assinatura</Typography>
              <Typography variant="body1">{s?.dateCreated ? new Date(s.dateCreated).toLocaleDateString('pt-BR') : '—'}</Typography>
            </Box>
            <Box>
              <Typography variant="body2" sx={{ color: DESIGN_TOKENS.colors.text.secondary, fontWeight: 700 }}>Próxima Cobrança</Typography>
              <Typography variant="body1">{s?.nextDueDate || '—'}</Typography>
            </Box>
          </Box>
        )}
        <Box sx={{ mt: 3 }}>
          <Button variant="secondary" onClick={() => window.history.back()}>Voltar</Button>
        </Box>
      </Card>
    </Box>
  );
};

export default SubscriptionDetailsPage;